/**
 * [INPUT]: 依赖 ./types ./geometry ./landmarks-map ./piecewise，依赖 ./geo_norm_params.json 参数表
 * [OUTPUT]: 对外提供 computeMetrics(8 原始几何量) / scoreMetric(单指标→子分+delta) / resolveParams(性别解析) / GeoNormParams 类型
 * [POS]: apps/web/src/scorer 的规则层核心，把 468 关键点 → 8 无量纲指标 → piecewise 子分；零副作用
 * [PROTOCOL]: 变更时更新此头部，然后检查 ../CLAUDE.md
 */
import type { Gender, GeoMetricKey, Landmark } from './types'
import { clamp01, distance, mean, midpoint, vertexAngleDeg, mirrorX } from './geometry'
import { at, midlineX, pupilDistance, pupils } from './landmarks-map'
import { piecewise, type PiecewiseParams } from './piecewise'
import geoParams from './geo_norm_params.json'

export type { PiecewiseParams }

export interface MetricParam extends PiecewiseParams {
  label: string
  direction: 'lower_better' | 'target' | 'higher_better'
  /** 人群典型值，用于 deltas 反事实基线（不性别化） */
  pop_avg?: number
  gender?: { male?: Partial<PiecewiseParams>; female?: Partial<PiecewiseParams> }
}

export interface RarityParams {
  /** 全 8 子同时接近 peak 时的最大协同奖励（叠在 base 之上，制造顶端陡崖） */
  bonus_max: number
  /** "近 peak"判定阈值 = ratio * peak */
  near_peak_ratio: number
  /** 低于此子分视为"严重缺陷"，惩罚协同奖励（防止一两指标爆表仍拿高 bonus） */
  terrible_threshold: number
}

export interface GeoNormParams {
  version: string
  scorer: string
  main_weights: Record<string, number>
  rarity: RarityParams
  metrics: Record<GeoMetricKey, MetricParam>
  confidence_weights: { pose: number; landmark: number; photo_quality: number }
}

export const PARAMS = geoParams as unknown as GeoNormParams

/** 性别解析：male/female 取覆盖；unknown 取男女覆盖的均值（无覆盖即原值）。peak 不性别化，取 base。 */
export function resolveParams(key: GeoMetricKey, gender: Gender): PiecewiseParams {
  const base = PARAMS.metrics[key]
  const p: PiecewiseParams = {
    ideal_low: base.ideal_low,
    ideal_high: base.ideal_high,
    soft_min: base.soft_min,
    soft_max: base.soft_max,
    peak: base.peak,
  }
  const ov = base.gender
  if (!ov) return p
  if (gender === 'male' && ov.male) return { ...p, ...ov.male }
  if (gender === 'female' && ov.female) return { ...p, ...ov.female }
  if (gender === 'unknown' && ov.male && ov.female) {
    const m = ov.male
    const f = ov.female
    return {
      peak: base.peak,
      ideal_low: avgNum(m.ideal_low, f.ideal_low, base.ideal_low),
      ideal_high: avgOpt(m.ideal_high, f.ideal_high, base.ideal_high),
      soft_min: avgOpt(m.soft_min, f.soft_min, base.soft_min),
      soft_max: avgOpt(m.soft_max, f.soft_max, base.soft_max),
    }
  }
  return p
}

/** 均值（必返回 number，用于 ideal_low）。 */
function avgNum(a?: number | null, b?: number | null, base: number = 0): number {
  if (a == null && b == null) return base
  if (a == null) return b as number
  if (b == null) return a as number
  return (a + b) / 2
}

/** 均值（可空，用于 ideal_high/soft_min/soft_max）。 */
function avgOpt(a?: number | null, b?: number | null, base: number | null = null): number | null {
  if (a == null && b == null) return base
  if (a == null) return b ?? null
  if (b == null) return a
  return (a + b) / 2
}

/** 计算 8 个原始几何量。landmarks 须为各向同性（像素）空间。 */
export function computeMetrics(landmarks: Landmark[]): Record<GeoMetricKey, number> {
  const pupilDist = pupilDistance(landmarks)
  const { left: leftPupil, right: rightPupil } = pupils(landmarks)
  const pupilMid = midpoint(leftPupil, rightPupil)
  const mouthMid = midpoint(at(landmarks, 'leftMouth'), at(landmarks, 'rightMouth'))
  const bizygomatic = distance(at(landmarks, 'leftCheek'), at(landmarks, 'rightCheek'))

  // 1. 三庭比例（中/下庭失衡比）：中庭 nasion→subnasale 与下庭 subnasale→gnathion 的相对偏离。
  //    不用发际线 trichion（随发型/额头/裁剪漂移，正脸也会被坑）；两段均为骨性稳定点。
  //    lower_better，0=中下庭等长（理想），与旧三庭语义一致，piecewise 参数不变。
  const midThird = Math.abs(at(landmarks, 'subnasale').y - at(landmarks, 'nasion').y)
  const lowerThird = Math.abs(at(landmarks, 'gnathion').y - at(landmarks, 'subnasale').y)
  const avgT = (midThird + lowerThird) / 2 || 1
  const facial_thirds = Math.abs(midThird - lowerThird) / avgT

  // 2. 中面比：瞳距 / (瞳孔中点到口角中点纵向距)
  const vertical = Math.abs(pupilMid.y - mouthMid.y) || 1
  const midface_ratio = pupilDist / vertical

  // 3. 眦角倾斜：外眦相对内眦的上扬角（度），正=上扬。统一用 (内眦y-外眦y)/|横距|，避免左右眼 dx 符号翻转
  const leftOuter = at(landmarks, 'leftEyeOuter')
  const leftInner = at(landmarks, 'leftEyeInner')
  const rightOuter = at(landmarks, 'rightEyeOuter')
  const rightInner = at(landmarks, 'rightEyeInner')
  const leftTilt = (Math.atan2(leftInner.y - leftOuter.y, Math.abs(leftInner.x - leftOuter.x)) * 180) / Math.PI
  const rightTilt = (Math.atan2(rightInner.y - rightOuter.y, Math.abs(rightInner.x - rightOuter.x)) * 180) / Math.PI
  const canthal_tilt = mean([leftTilt, rightTilt])

  // 4. 瞳距比：瞳距 / 颧宽
  const interpupillary = pupilDist / (bizygomatic || 1)

  // 5. 下颌角：在 gonion 处，cheek→gonion→gnathion 张角
  const gonialLeft = vertexAngleDeg(at(landmarks, 'leftCheek'), at(landmarks, 'leftGonion'), at(landmarks, 'gnathion'))
  const gonialRight = vertexAngleDeg(at(landmarks, 'rightCheek'), at(landmarks, 'rightGonion'), at(landmarks, 'gnathion'))
  const gonial_angle = mean([gonialLeft, gonialRight])

  // 6. 下颌宽/颧宽
  const bigonial = distance(at(landmarks, 'leftGonion'), at(landmarks, 'rightGonion'))
  const bigonial_bizy = bigonial / (bizygomatic || 1)

  // 7. 对称性：左右配对点关于中线的镜像误差倒数（按瞳距归一）
  const mid = midlineX(landmarks)
  const pairs: Array<[GeoMetricKey, GeoMetricKey]> = [
    (['leftEyeOuter', 'rightEyeOuter'] as unknown as [GeoMetricKey, GeoMetricKey]),
    (['leftEyeInner', 'rightEyeInner'] as unknown as [GeoMetricKey, GeoMetricKey]),
    (['leftCheek', 'rightCheek'] as unknown as [GeoMetricKey, GeoMetricKey]),
    (['leftGonion', 'rightGonion'] as unknown as [GeoMetricKey, GeoMetricKey]),
    (['leftMouth', 'rightMouth'] as unknown as [GeoMetricKey, GeoMetricKey]),
    (['leftJaw', 'rightJaw'] as unknown as [GeoMetricKey, GeoMetricKey]),
  ]
  const symDevs: number[] = []
  for (const [lk, rk] of pairs) {
    const l = at(landmarks, lk as never)
    const r = at(landmarks, rk as never)
    symDevs.push(distance(l, mirrorX(r, mid)) / (pupilDist || 1))
  }
  const symmetry_index = clamp01(1 - mean(symDevs))

  // 8. 下颌线定义度（2D 代理）：下颌角点到颏点的距离 / 面高。
  //    独立于 gonial_angle（下颌角张角）与 bigonial_bizy（下颌宽），
  //    衡量下颌缘的纵向延展——越长越清晰。注：2D 无法度量真正的下颌立体感，仅为代理。
  const faceHeight = distance(at(landmarks, 'nasion'), at(landmarks, 'gnathion')) || 1
  const jawLen = mean([
    distance(at(landmarks, 'leftGonion'), at(landmarks, 'gnathion')),
    distance(at(landmarks, 'rightGonion'), at(landmarks, 'gnathion')),
  ])
  const jaw_sharpness = jawLen / faceHeight

  return {
    facial_thirds,
    midface_ratio,
    canthal_tilt,
    interpupillary,
    gonial_angle,
    bigonial_bizy,
    symmetry_index,
    jaw_sharpness,
  }
}

export interface MetricScore {
  key: GeoMetricKey
  value: number
  sub: number // 0..1
  label: string
  ideal: string
}

function idealStr(p: PiecewiseParams): string {
  if (p.ideal_high === null) return `≥${p.ideal_low}`
  return `${p.ideal_low}–${p.ideal_high}`
}

export function scoreMetric(key: GeoMetricKey, value: number, gender: Gender): MetricScore {
  const p = resolveParams(key, gender)
  return {
    key,
    value,
    sub: piecewise(value, p),
    label: PARAMS.metrics[key].label,
    ideal: idealStr(p),
  }
}